import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authService, AuthApiError, User, type UpdateActivityData } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../services/httpClient';
import { isValidReturnUrl } from '../utils/urlValidation';

// Auth event constants
export const AUTH_EVENTS = {
  TOKEN_REFRESHED: 'tokenRefreshed',
  AUTH_FAILURE: 'authFailure',
} as const;

// Auth event detail types
export interface TokenRefreshedEventDetail {
  user: User | null;
}

export interface AuthFailureEventDetail {
  // Add specific failure details if needed in the future
}

// Typed event interfaces
export interface TokenRefreshedEvent extends CustomEvent<TokenRefreshedEventDetail> {
  type: typeof AUTH_EVENTS.TOKEN_REFRESHED;
}

export interface AuthFailureEvent extends CustomEvent<AuthFailureEventDetail> {
  type: typeof AUTH_EVENTS.AUTH_FAILURE;
}

// Typed event listener wrapper functions
export function addTokenRefreshedListener(
  listener: (event: TokenRefreshedEvent) => void
): void {
  window.addEventListener(AUTH_EVENTS.TOKEN_REFRESHED, listener as EventListener);
}

export function removeTokenRefreshedListener(
  listener: (event: TokenRefreshedEvent) => void
): void {
  window.removeEventListener(AUTH_EVENTS.TOKEN_REFRESHED, listener as EventListener);
}

export function addAuthFailureListener(
  listener: (event: AuthFailureEvent) => void
): void {
  window.addEventListener(AUTH_EVENTS.AUTH_FAILURE, listener as EventListener);
}

export function removeAuthFailureListener(
  listener: (event: AuthFailureEvent) => void
): void {
  window.removeEventListener(AUTH_EVENTS.AUTH_FAILURE, listener as EventListener);
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (firstName: string, lastName: string, email: string, password: string) => Promise<void>;
  setGuestAuth: (token: string, user: User) => void;
  logout: () => void;
  updateActivity: (data: UpdateActivityData) => Promise<void>;
  updateUserData: (user: User) => void;
  refreshUserData: () => Promise<User>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<User>;
  handleAuthError: (error: Error) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();

  // Set up navigation function for httpClient
  useEffect(() => {
    httpClient.setNavigationFunction((path: string, options?: { replace?: boolean }) => {
      navigate(path, options);
    });
  }, [navigate]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Development mode initialization - no logging needed
        
        // Initialize auth state from storage
        const currentUser = authService.getUser();
        const isAuthenticated = authService.isAuthenticated();

        
        if (isAuthenticated && currentUser) {
          // User is authenticated and current user data exists
          setUser(currentUser);
        } else if (currentUser && authService.getToken()) {
          // User exists but token might be expired - try to refresh
          try {
            await authService.attemptTokenRefresh();
            const refreshedUser = authService.getUser();
            const refreshedAuth = authService.isAuthenticated();
            

            
            if (refreshedUser && refreshedAuth) {
              // Token refresh successful
              setUser(refreshedUser);
            } else {
              console.warn('🔴 AuthContext: Token refresh succeeded but user not authenticated, logging out');
              authService.logout();
            }
          } catch (error) {
            console.warn('🔴 AuthContext: Failed to refresh token on initialization:', error);
            
            // Check if this is a registration incomplete error
            if (error instanceof Error && error.message.includes('Registration incomplete')) {
              // Clear auth state but don't redirect - let the user complete registration
              authService.logout();
              setUser(null);
            } else {
              // For other errors, clear invalid session
              authService.logout();
            }
          }
        } else {
          // No existing authentication state found
        }
      } catch (error) {
        console.error('🔴 AuthContext: Auth initialization error:', error);
        // Clear any invalid state
        authService.logout();
      } finally {
        // Authentication initialization complete
        setIsInitialized(true);
        setIsLoading(false);
      }
    };
    
    initializeAuth();

    // Listen for HTTP client auth events
    const handleTokenRefreshed = (event: TokenRefreshedEvent): void => {
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('🔄 AuthContext: Token refreshed by HTTP client');
      }
      const updatedUser = event.detail?.user;
      if (updatedUser) {
        setUser(updatedUser);
        authService.updateUserData(updatedUser);
      }
    };

    const handleAuthFailure = (event: AuthFailureEvent): void => {
      const logData: Record<string, any> = {
        currentPath: window.location.pathname,
        timestamp: new Date().toISOString()
      };

      // Only include sensitive event details in development
      if (process.env['NODE_ENV'] === 'development') {
        logData['eventDetail'] = event.detail;
      }

      console.warn('🔴 AuthContext: Auth failure from HTTP client', logData);
      setUser(null);
      // Navigation will be handled by HTTP client
    };

    // Add event listeners for HTTP client integration
    addTokenRefreshedListener(handleTokenRefreshed);
    addAuthFailureListener(handleAuthFailure);

    return () => {
      removeTokenRefreshedListener(handleTokenRefreshed);
      removeAuthFailureListener(handleAuthFailure);
    };
  }, []);

  // Handle authentication errors globally
  const handleAuthError = useCallback((error: Error) => {
    // Check if error message indicates authentication issues
    const authErrorMessages = [
      'Authentication required',
      'Your account has been disabled',
      'Your account access has expired',
      'Your session has expired',
      'Session duration exceeded'
    ];

    const isAuthError = authErrorMessages.some(message => 
      error.message.includes(message)
    );

    if (isAuthError) {
      // Preserve current location for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      const isSessionExpired = error.message.includes('Session duration exceeded') ||
                              error.message.includes('session has expired') ||
                              error.message.includes('SESSION_EXPIRED');
      
      setUser(null);
      
      // Don't preserve return URL for login or profile pages to avoid loops
      const shouldPreserveUrl = currentPath !== '/login' && 
                               !currentPath.startsWith('/login?') &&
                               currentPath !== '/profile' && 
                               currentPath !== '/profile/edit';
      
      if (shouldPreserveUrl && isSessionExpired) {
        // Redirect with session expiration flag and return URL
        const loginUrl = `/login?sessionExpired=true&returnUrl=${encodeURIComponent(currentPath)}`;
        navigate(loginUrl, { replace: true });
      } else {
        // Regular login redirect without return URL
        navigate('/login', { replace: true });
      }
    }
  }, [navigate]);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const user = await authService.login(email, password);
      setUser(user);
      
      // Check for return URL after successful login
      const returnUrl = sessionStorage.getItem('returnUrl');
      if (returnUrl && isValidReturnUrl(returnUrl)) {
        sessionStorage.removeItem('returnUrl');
        // Navigate immediately after validation - no setTimeout needed
        navigate(returnUrl, { replace: true });
      }
      
      return user;
    } catch (error) {
      handleAuthError(error as Error);
      throw error;
    }
  };

  const register = async (firstName: string, lastName: string, email: string, password: string): Promise<void> => {
    try {
      const user = await authService.register(firstName, lastName, email, password);
      setUser(user);
    } catch (error) {
      if (error instanceof AuthApiError) {
        throw error;
      }
      throw new Error('An unexpected error occurred during registration');
    }
  };

  const logout = (): void => {
    authService.logout();
    setUser(null);
    navigate('/login', { replace: true });
  };

  const updateActivity = useCallback(async (data: UpdateActivityData): Promise<void> => {
    try {
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('Updating user activity:', Object.keys(data))
      }
      const updatedUser = await authService.updateActivity(data);
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('User activity updated successfully')
      }
      setUser(updatedUser);
    } catch (error) {
      console.error('Error updating user activity:', error)
      handleAuthError(error as Error);
      if (error instanceof AuthApiError) {
        throw error;
      }
      throw new Error('An unexpected error occurred updating user activity');
    }
  }, [handleAuthError]);

  const forgotPassword = async (email: string): Promise<void> => {
    try {
      await authService.forgotPassword(email);
    } catch (error) {
      handleAuthError(error as Error);
      throw error;
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<void> => {
    try {
      const user = await authService.resetPassword(token, newPassword);
      setUser(user);
    } catch (error) {
      handleAuthError(error as Error);
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<User> => {
    try {
      const updatedUser = await authService.changePassword(currentPassword, newPassword);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      handleAuthError(error as Error);
      throw error;
    }
  };

  const setGuestAuth = (token: string, user: User): void => {
    authService.setAuthenticationData(token, user);
    setUser(user);
  };

  const isAuthenticated = (): boolean => {
    return authService.isAuthenticated();
  };

  const updateUserData = (user: User): void => {
    authService.updateUserData(user);
    setUser(user);
  };

  const refreshUserData = async (): Promise<User> => {
    try {
      const updatedUser = await authService.refreshUserData();
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      handleAuthError(error as Error);
      throw error;
    }
  };

  // Listen for profile completion changes and refresh user data
  useEffect(() => {
    let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
    
    const handleProfileCompletionChanged = async () => {
      // Debounce rapid events to prevent excessive API calls
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      
      debounceTimeout = setTimeout(async () => {
        // Profile completion refresh started
        
        try {
          await refreshUserData();
          // Profile completion refresh completed
        } catch (error) {
          console.error('🔴 AuthContext: Failed to refresh user data after profile completion:', error);
          // Don't call handleAuthError here as it could log the user out
          // The profile completion event should still work even if refresh fails
          if (import.meta.env.DEV) {
            console.warn('🟡 AuthContext: Continuing without refresh - profile completion may need manual refresh');
          }
        }
      }, 300); // 300ms debounce
    };

    window.addEventListener('profileCompletionChanged', handleProfileCompletionChanged);
    
    return () => {
      window.removeEventListener('profileCompletionChanged', handleProfileCompletionChanged);
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: isAuthenticated(),
    isLoading,
    login,
    register,
    setGuestAuth,
    logout,
    updateActivity,
    updateUserData,
    refreshUserData,
    forgotPassword,
    resetPassword,
    changePassword,
    handleAuthError,
  };

  // Show loading state until initialization is complete
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  
  // More defensive context checking with better error handling
  if (context === undefined) {
    // Always log basic error for production debugging
    console.error('🔴 useAuth: Context is undefined - this may be due to navigation timing issues');
    
    // Log detailed debugging information in development only
    if (import.meta.env.DEV) {
      console.error('🔴 useAuth: Context is undefined', {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        pathname: window.location.pathname,
        userAgent: navigator.userAgent.substring(0, 100),
        stackTrace: new Error().stack
      });
      
      console.error('🔴 useAuth: This usually happens due to:');
      console.error('  1. Component rendering before AuthProvider is mounted');
      console.error('  2. Race condition during navigation');
      console.error('  3. Context provider being unmounted unexpectedly');
      console.error('  4. Component being rendered outside the provider tree');
      console.error('  5. Lazy loading timing issues with React Suspense');
    }
    
    // IMPROVED ERROR HANDLING: Add retry mechanism for navigation timing issues
    // Instead of immediately throwing, try to recover gracefully
    const retryKey = 'authContextRetry';
    const retryCount = parseInt(sessionStorage.getItem(retryKey) || '0');
    
    if (retryCount < 3) {
      // Increment retry count
      sessionStorage.setItem(retryKey, (retryCount + 1).toString());
      
      if (import.meta.env.DEV) {
        console.warn(`🟡 useAuth: Attempting recovery (retry ${retryCount + 1}/3)`);
      }
      
      // Schedule a re-render to try again
      setTimeout(() => {
        // Force a page refresh as last resort if multiple retries fail
        if (retryCount >= 2) {
          console.warn('🔴 useAuth: Multiple context failures detected, refreshing page');
          window.location.reload();
        }
      }, 100);
      
      // Return a fallback context to prevent crashes
      return {
        user: null,
        isAuthenticated: false,
        isLoading: true,
        login: async () => { throw new Error('AuthContext not available'); },
        register: async () => { throw new Error('AuthContext not available'); },
        setGuestAuth: () => { throw new Error('AuthContext not available'); },
        logout: () => { window.location.href = '/login'; },
        updateActivity: async () => { throw new Error('AuthContext not available'); },
        updateUserData: () => { throw new Error('AuthContext not available'); },
        refreshUserData: async () => { throw new Error('AuthContext not available'); },
        forgotPassword: async () => { throw new Error('AuthContext not available'); },
        resetPassword: async () => { throw new Error('AuthContext not available'); },
        changePassword: async () => { throw new Error('AuthContext not available'); },
        handleAuthError: () => { window.location.href = '/login'; },
      };
    } else {
      // Clear retry count and throw error after max attempts
      sessionStorage.removeItem(retryKey);
      throw new Error('useAuth must be used within an AuthProvider. If you see this error after navigation, try refreshing the page.');
    }
  }
  
  // Clear retry count on successful context access
  sessionStorage.removeItem('authContextRetry');
  
  return context;
} 